import React, { createContext, Dispatch, useEffect, useRef } from 'react';
import { MandatoryChildren } from '../../../models/children-props';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useEffectReducer } from 'use-effect-reducer';
import { ActivePostEditorTabType, PostEditorGroupDataActions } from './PostEditorGroupDataActions';
import { PostEditorGroupDataEffects } from './PostEditorGroupDataEffects';
import { PostEditorGroupDataReducer } from './PostEditorGroupDataReducer';
import { DeepReadonly } from '../../../models/basic-objects';
import { GENERATING_CAPTIONS_PLACEHOLDER } from './initial';
import {
  GetCaptionsLazyQueryHookResult,
  InitiateCaptionsMutationFn,
  StopCaptionsMutationFn,
  useGetCaptionsLazyQuery,
  useInitiateCaptionsMutation,
  useStopCaptionsMutation
} from '../../../models/graphql';

export enum CaptionWriterViewType {
  initial = 1,
  results
}

export type AiCaptionType = {
  viewType: CaptionWriterViewType;
  loading: boolean;
  cacheKey: string;
  prompt: string;
  promptLabel: string;
  propmptBase: string;
  captionResults: string;
  getCaptionsQuery: GetCaptionsLazyQueryHookResult[0];
  initiateCaptionsMutation: InitiateCaptionsMutationFn;
  stopCaptionsMutation: StopCaptionsMutationFn;
  forceStopped: React.MutableRefObject<boolean> | null;
  limitReached: boolean;
};

export type PostEditorGroupDataContextType = [
  state: PostEditorGroupDataType,
  dispatch: Dispatch<PostEditorGroupDataActions>
];
export type PostEditorGroupDataType = DeepReadonly<{
  tabs: ActivePostEditorTabType;
  postGroupIndex: number;
  aiCaptionWriter: AiCaptionType;
}>;

const initialCaptionWriter: AiCaptionType = {
  viewType: CaptionWriterViewType.initial,
  loading: false,
  cacheKey: '',
  prompt: '',
  promptLabel: 'Or write your own prompt',
  propmptBase: '',
  captionResults: GENERATING_CAPTIONS_PLACEHOLDER,
  getCaptionsQuery: () =>
    new Promise(_ => {
      /**/
    }),
  initiateCaptionsMutation: () => new Promise(resolve => resolve({})),
  stopCaptionsMutation: () => new Promise(resolve => resolve({})),
  forceStopped: null,
  limitReached: false
};

export const PostEditorGroupDataContext = createContext<PostEditorGroupDataContextType>([
  {
    tabs: {
      tabsType: undefined,
      leftTabsIndex: undefined,
      rightTabsIndex: undefined
    },
    postGroupIndex: 0,
    aiCaptionWriter: initialCaptionWriter
  },
  () => {
    /**/
  }
]);

const getInitialValue = (
  type: ActivePostEditorTabType['tabsType'],
  postGroupIndex: number
): PostEditorGroupDataType => {
  const tabs =
    type === 'single'
      ? {
          tabsType: type,
          leftTabsIndex: 0,
          rightTabsIndex: undefined
        }
      : { tabsType: type, leftTabsIndex: 0, rightTabsIndex: 0 };
  return {
    tabs,
    postGroupIndex,
    aiCaptionWriter: initialCaptionWriter
  };
};

export type PostEditorGroupIndexProps = {
  postGroupIndex: number;
};

export const PostEditorGroupDataProvider = ({
  children,
  postGroupIndex
}: MandatoryChildren<PostEditorGroupIndexProps>): JSX.Element => {
  const breakpoint = useBreakpoints();
  const type: ActivePostEditorTabType['tabsType'] = ['small', 'medium'].includes(breakpoint) ? 'single' : 'double';
  const [initiateCaptionsMutation] = useInitiateCaptionsMutation();
  const [stopCaptionsMutation] = useStopCaptionsMutation();
  const [getCaptionsQuery] = useGetCaptionsLazyQuery();
  const initialValue = getInitialValue(type, postGroupIndex);
  const reducer = useEffectReducer<PostEditorGroupDataType, PostEditorGroupDataActions>(
    PostEditorGroupDataReducer,
    initialValue,
    PostEditorGroupDataEffects
  );
  const forcedStopRef = useRef(false);
  const [_, dispatch] = reducer;
  useEffect(() => {
    dispatch({ type: 'setForceStopped', payload: forcedStopRef });
  }, [dispatch]);
  useEffect(() => {
    dispatch({
      type: 'setAiGqlFunctions',
      payload: { initiateCaptionsMutation, stopCaptionsMutation, getCaptionsQuery }
    });
  }, [stopCaptionsMutation, getCaptionsQuery, initiateCaptionsMutation, dispatch]);

  useEffect(() => {
    dispatch({ type: 'setPostGroupIndex', payload: postGroupIndex });
  }, [postGroupIndex, dispatch]);

  useEffect(() => {
    dispatch({ type: 'setType', payload: type });
  }, [dispatch, type]);

  return <PostEditorGroupDataContext.Provider value={reducer}>{children}</PostEditorGroupDataContext.Provider>;
};
